import { ACTIVITY_CATEGORIES, ACTIVITY_TYPES } from './constants';
import { ROLE_LABELS } from '../../helpers/projectRoles';

/**
 * Maps the API payloads onto the segment model the Comm Center renders
 * (the segment kinds are documented on RichText).
 *
 * The API stores a message as plain text plus the cue codes and the mentions it
 * refers to; the sidebar renders cue tags and mentions as interactive chips, so
 * the text is split back into segments here.
 */

/** `1m2` and friends: what the backend extracts into `cueRefs`. */
const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const MENTION_RE = /@[\p{L}\p{N}._-]+/gu;

/**
 * Splits a plain body into text / cue / mention segments.
 *
 * A code is resolved onto the cue the message itself points at when it can:
 * `cueSheetItemIds` holds the ids the backend resolved (or the composer sent),
 * and that is the only way to tell two cues sharing a label apart. The label
 * index is the fallback, and on an homonym it lands on one of them.
 *
 * @param {string} body
 * @param {array}  cueRefs   cue codes the backend found in the text
 * @param {object} lookup    { cueByCode, cueById, userByName } used to enrich the chips
 * @param {array}  cueIds    ids of the cues the message refers to
 */
export const toSegments = (body, cueRefs = [], lookup = {}, cueIds = []) => {
    const { cueByCode = {}, cueById = {}, userByName = {} } = lookup;
    const text = String(body || '');

    const cueFor = (code) => {
        const key = String(code).toLowerCase();
        const linked = (cueIds || [])
            .map(id => cueById[id])
            .find(cue => cue && String(cue.code).toLowerCase() === key);

        return linked || cueByCode[key] || null;
    };

    // One pass over the string, collecting the ranges that become a chip.
    const marks = [];

    (cueRefs || []).forEach(code => {
        if (!code) return;

        const re = new RegExp(`(^|[^\\p{L}\\p{N}])(${escapeRegExp(code)})(?![\\p{L}\\p{N}])`, 'giu');
        const cue = cueFor(code);
        let match;

        while ((match = re.exec(text)) !== null) {
            const start = match.index + match[1].length;

            marks.push({
                start,
                end: start + match[2].length,
                segment: {
                    k: 'cue',
                    code: match[2],
                    instrument: cue?.instrument || null,
                    cueId: cue?.id || null,
                },
            });
        }
    });

    let mention;
    MENTION_RE.lastIndex = 0;

    while ((mention = MENTION_RE.exec(text)) !== null) {
        marks.push({
            start: mention.index,
            end: mention.index + mention[0].length,
            segment: {
                k: 'mention',
                v: mention[0],
                userId: userByName[mention[0].slice(1).toLowerCase()] || null,
            },
        });
    }

    if (!marks.length) {
        return text ? [{ k: 'text', v: text }] : [];
    }

    marks.sort((a, b) => a.start - b.start);

    const segments = [];
    let cursor = 0;

    marks.forEach(mark => {
        if (mark.start < cursor) return; // overlapping match, keep the first one

        if (mark.start > cursor) {
            segments.push({ k: 'text', v: text.slice(cursor, mark.start) });
        }

        segments.push(mark.segment);
        cursor = mark.end;
    });

    if (cursor < text.length) {
        segments.push({ k: 'text', v: text.slice(cursor) });
    }

    return segments;
}

/**
 * Cue codes of the project that appear in a message being written, so the
 * composer can send them as `cue_refs` and the backend can index the thread
 * against the right cues.
 */
export const detectCueRefs = (text, cueByCode = {}) => {
    const body = String(text || '');

    return Object.values(cueByCode)
        .map(cue => cue.code)
        .filter(code => new RegExp(`(^|[^\\p{L}\\p{N}])${escapeRegExp(code)}(?![\\p{L}\\p{N}])`, 'iu').test(body));
}

const toCueEntry = (item) => ({
    id: item.id,
    code: item.label || item.title,
    instrument: item.cueType || null,
    // `instanceDuration` and the clip duration are both milliseconds.
    duration: (item.instanceDuration || item.clip?.duration || 0) / 1000 || null,
    url: item.clip?.url || null,
});

/**
 * Index of the project cue sheet by the code a message can refer to, so a
 * `cueRefs` entry can be turned into a cue tag that navigates somewhere.
 * Labels are not unique: on an homonym the last cue wins, which is why a
 * message's own `cueSheetItemIds` are looked up in buildCueIdIndex first.
 */
export const buildCueIndex = (cueSheet = []) => {
    const index = {};

    (Array.isArray(cueSheet) ? cueSheet : []).forEach(item => {
        const code = item.label || item.title;

        if (!code) return;

        index[String(code).toLowerCase()] = toCueEntry(item);
    });

    return index;
}

/** The same entries by cue id, which — unlike the label — is unique. */
export const buildCueIdIndex = (cueSheet = []) => {
    const index = {};

    (Array.isArray(cueSheet) ? cueSheet : []).forEach(item => {
        if (item.id && (item.label || item.title)) index[item.id] = toCueEntry(item);
    });

    return index;
}

/**
 * The ids to send as `cue_sheet_item_ids` for the codes found in a message.
 *
 * The backend resolves a code on its own when the label is unique in the
 * project, and deliberately leaves an homonym unresolved rather than guess.
 * The composer knows more than the text does: the cues selected on the page,
 * the thread being replied to, the activity the comment was started from. So
 * an homonym is resolved onto the candidates among `preferredIds`, and stays
 * unresolved — no id sent — when none of them is a candidate.
 *
 * @param {string[]} codes         cue codes found in the text
 * @param {array}    cueSheet      the project cue sheet
 * @param {string[]} preferredIds  cues the context points at
 */
export const resolveCueIds = (codes = [], cueSheet = [], preferredIds = []) => {
    const items = Array.isArray(cueSheet) ? cueSheet : [];
    const ids = new Set();

    (codes || []).forEach(code => {
        const key = String(code || '').toLowerCase();

        if (!key) return;

        const candidates = items.filter(item => String(item.label || item.title || '').toLowerCase() === key);

        if (candidates.length === 1) {
            ids.add(candidates[0].id);
            return;
        }

        candidates
            .filter(item => (preferredIds || []).includes(item.id))
            .forEach(item => ids.add(item.id));
    });

    return [...ids];
}

/** Index of the team by first name, to resolve `@Marco` onto a user id. */
export const buildUserIndex = (team = []) => {
    const index = {};

    (Array.isArray(team) ? team : []).forEach(member => {
        if (!member.userId) return;

        const full = `${member.name || ''} ${member.surname || ''}`.trim();

        [member.name, full].forEach(key => {
            if (key) index[String(key).toLowerCase()] = member.userId;
        });
    });

    return index;
}

/**
 * Does a card belong to the current selection, judged on the cue *codes*?
 *
 * The selected items are reduced to their codes and matched case
 * insensitively. A card matches as soon as one of its codes is in the scope: a
 * comment about "1m2 e 1m3" stays visible with either of the two selected.
 *
 * Labels are not unique, so this matches an homonym too; it is the fallback for
 * whatever carries no id (see matchesScope, matchesMessageScope).
 *
 * @param {string[]} codes  lower case codes of the selection
 * @param {string|string[]} refs  what the card refers to
 */
export const matchesCueScope = (codes, refs) => {
    const list = Array.isArray(refs) ? refs : [refs];

    return list.some(ref => Boolean(ref) && codes.includes(String(ref).toLowerCase()));
}

/**
 * Does an activity belong to the current selection?
 *
 * An activity now carries the entity it is about — `cueSheetItemId` for a cue,
 * `entityType` + `entityId` for a video, a rec session, a track or a clip — so
 * the match is made on ids, which the labels never allowed (two cues can share
 * one). Activities created before the backfill still carry only `cueRef`, and
 * those fall back to the code comparison.
 *
 * @param {object} scope     { entityType, ids, codes } of the selection
 * @param {object} activity  as returned by toActivity
 */
export const matchesScope = (scope, activity) => {
    const { entityType, ids = [], codes = [] } = scope || {};

    if (entityType === 'cue_sheet_item') {
        if (activity.cueSheetItemId) return ids.includes(activity.cueSheetItemId);
        if (activity.entityType === 'cue_sheet_item' && activity.entityId) {
            return ids.includes(activity.entityId);
        }

        return matchesCueScope(codes, activity.cueRef);
    }

    return activity.entityType === entityType && ids.includes(activity.entityId);
}

/**
 * Does a message belong to the current selection?
 *
 * A message carries `cueSheetItemIds` — the cues its codes were resolved onto —
 * and when it has them the match is on ids, so an homonym no longer drags in
 * every cue sharing the label. A message without ids (an unresolved homonym,
 * or a semantic search hit) falls back to its codes.
 *
 * Only a cue selection can scope the Comunicazioni tab: a message never refers
 * to a video or a rec session.
 *
 * @param {object} scope    { entityType, ids, codes } of the selection
 * @param {object} comment  as returned by toComment
 */
export const matchesMessageScope = (scope, comment) => {
    const { entityType, ids = [], codes = [] } = scope || {};

    if (entityType !== 'cue_sheet_item') return false;

    const linked = comment.cueSheetItemIds || [];

    if (linked.length) return linked.some(id => ids.includes(id));

    return matchesCueScope(codes, comment.cueRefs || []);
}

const displayName = (member) => [member?.name, member?.surname].filter(Boolean).join(' ');

/**
 * MessageRead -> the comment shape the CommentCard renders.
 *
 * `repliesCount` is the counter the list endpoint carries; the thread itself is
 * fetched on demand by CommentReplies from `GET /communications/{id}/replies`.
 */
export const toComment = (message, lookup = {}) => ({
    id: message.id,
    author: {
        name: message.authorLabel,
        role: ROLE_LABELS[message.authorRole] || message.authorRole || null,
        userId: message.authorUserId || null,
    },
    // `in_app` is "written here" and carries no badge; the rest are inbound.
    source: message.source && message.source !== 'in_app' ? message.source : null,
    resolved: Boolean(message.resolved),
    createdAt: message.createdAt,
    body: toSegments(message.body, message.cueRefs, lookup, message.cueSheetItemIds),
    rawBody: message.body,
    cueRefs: message.cueRefs || [],
    cueSheetItemIds: message.cueSheetItemIds || [],
    repliesCount: message.replies || 0,
    draft: null,
});

/** MessageRead (a reply) -> the row the CommentReplies list renders. */
export const toReply = (message) => ({
    id: message.id,
    author: {
        name: message.authorLabel,
        role: ROLE_LABELS[message.authorRole] || message.authorRole || null,
        userId: message.authorUserId || null,
    },
    text: message.body,
    createdAt: message.createdAt,
});

const ACTIVITY_TYPE_MAP = {
    assigned_task: { type: ACTIVITY_TYPES.CUE_ASSIGNED, category: ACTIVITY_CATEGORIES.EDITS },
    comment: { type: ACTIVITY_TYPES.COMMENTS, category: ACTIVITY_CATEGORIES.COMMENTS },
    team_join: { type: ACTIVITY_TYPES.TEAM_JOINED, category: ACTIVITY_CATEGORIES.TEAM },
    completed_cue: { type: ACTIVITY_TYPES.CUE_COMPLETED, category: ACTIVITY_CATEGORIES.COMPLETED },
    cue_updated: { type: ACTIVITY_TYPES.CUE_UPDATED, category: ACTIVITY_CATEGORIES.EDITS },
    deadline: { type: ACTIVITY_TYPES.DEADLINE, category: ACTIVITY_CATEGORIES.DEADLINES },
};

/**
 * ActivityRead (status `draft`) -> the inline draft an ActivityDraft renders.
 *
 * `createdBy` is what decides whether a musician may still edit it: the backend
 * only lets them touch the drafts they generated themselves.
 */
export const toDraft = (activity) => ({
    activityId: activity.id,
    createdBy: activity.createdBy,
    assigneeId: activity.assigneeUserId || null,
    name: activity.name || null,
    text: activity.body || '',
    cue: activity.cueRef ? { code: activity.cueRef } : null,
    createdAt: activity.createdAt,
    editing: false,
});

/**
 * The activity a comment produced, as the badge on the CommentCard renders it:
 * enough to say what became of the comment and to jump to the card itself.
 */
export const toLinkedActivity = (activity, lookup = {}) => {
    const assignee = (lookup.team || []).find(member => member.userId === activity.assigneeUserId);

    return {
        id: activity.id,
        type: activity.type,
        status: activity.status,
        assigneeName: assignee ? displayName(assignee) : null,
    };
}

/**
 * ActivityRead -> the activity shape the ActivityCard renders.
 *
 * An activity completed by the backend keeps the `completed_cue` look even when
 * it started life as an `assigned_task`, so the feed reads like the design.
 */
export const toActivity = (activity, lookup = {}) => {
    const { team = [], currentUserId = null, projectsById = {} } = lookup;
    const mapped = ACTIVITY_TYPE_MAP[activity.type] || ACTIVITY_TYPE_MAP.comment;
    // Only a task swaps its look when it closes. The informative events — a new
    // comment, a team join, a cue edit — are emitted already `completed` by the
    // backend, and relabelling those as "cue completata" is what made every
    // event in the feed look the same.
    const isCompleted = activity.type === 'assigned_task' && activity.status === 'completed';

    const assignee = team.find(member => member.userId === activity.assigneeUserId);
    // The id first: on an homonym the label would pick any cue sharing it.
    const resolvedCue = (activity.cueSheetItemId && lookup.cueById?.[activity.cueSheetItemId])
        || (activity.cueRef ? lookup.cueByCode?.[String(activity.cueRef).toLowerCase()] || null : null);
    // The preview only appears when the cue reference matches a cue sheet item
    // that actually carries audio.
    const cue = resolvedCue?.url
        ? { ...resolvedCue, code: activity.cueRef || resolvedCue.code, cueId: resolvedCue.id }
        : null;

    const project = projectsById[activity.projectId];

    return {
        id: activity.id,
        type: isCompleted ? ACTIVITY_TYPES.CUE_COMPLETED : mapped.type,
        // What the card is about. `cueSheetItemId` and `entityType`/`entityId`
        // are the reliable link — `cueRef` is the cue *code*, kept for display
        // and as the fallback for the activities that predate the backfill.
        cueRef: activity.cueRef || null,
        cueSheetItemId: activity.cueSheetItemId || null,
        entityType: activity.entityType || null,
        entityId: activity.entityId || null,
        // A custom title given to the activity; the card falls back to naming
        // the event from its type when it is missing.
        name: activity.name || null,
        // What the backend called it, before the completed task took on the
        // look of a completed cue: the card names the event from this.
        sourceType: activity.type,
        category: isCompleted ? ACTIVITY_CATEGORIES.COMPLETED : mapped.category,
        showIcon: true,
        status: activity.status,
        projectId: activity.projectId,
        assigneeUserId: activity.assigneeUserId || null,
        assigneeName: assignee ? displayName(assignee) : null,
        sourceMessageId: activity.sourceMessageId || null,
        createdBy: activity.createdBy,
        createdAt: activity.createdAt,
        cue,
        // The activity body is authored by the LLM and already names the cue.
        body: [
            ...(assignee ? [{ k: 'strong', v: displayName(assignee) }, { k: 'text', v: ' ' }] : []),
            ...toSegments(activity.body, activity.cueRef ? [activity.cueRef] : [], lookup),
            ...(project ? [{ k: 'text', v: ' · ' }, { k: 'project', v: project.title, projectId: project.id }] : []),
        ],
        rawBody: activity.body,
        // `submit-review` is what puts an activity here: the assignee handed the
        // work back and somebody with the role has to close it.
        needsReview: activity.status === 'in_review',
        isMine: Boolean(currentUserId) && activity.assigneeUserId === currentUserId,
        // Read / archived are per user flags the API now keeps for us.
        unread: !activity.read,
        archived: Boolean(activity.archived),
    };
}
